import {
  makeCombinedSearchExpressionValueToken,
  searchExpressionSpacedValueCanExtend,
  searchExpressionTokenText,
  normalizeSearchExpressionSpan,
  searchExpressionRawSlice,
  searchExpressionTokenLower,
  spanForSearchExpressionToken,
  tokenizeSearchExpressionDsl,
} from "./lex.js";

export class SearchExpressionBooleanParser {
  constructor(input, options = {}) {
    this.source = String(input ?? "");
    this.tokens = tokenizeSearchExpressionDsl(this.source);
    this.options = options || {};
    this.index = 0;
    this.issues = [];
    this.leadingExpressionEnd = null;
    this.implicitOrIssueSeverity = "warning";
    this.implicitOrMessage = "Adjacent terms are interpreted as OR. Use an explicit operator.";
  }

  current() {
    return this.tokens[this.index] || null;
  }

  previousEnd() {
    return this.tokens[this.index - 1]?.end ?? this.current()?.start ?? this.source.length;
  }

  currentSpan() {
    return spanForSearchExpressionToken(this.current(), this.source.length);
  }

  addIssue({
    stage = "parse",
    severity = "error",
    code,
    span = this.currentSpan(),
    raw,
    expected = [],
    message,
  }) {
    const normalized = normalizeSearchExpressionSpan(span, this.source);
    const issue = {
      stage,
      severity,
      code,
      span: normalized,
      raw: raw ?? searchExpressionRawSlice(this.source, normalized),
      message,
    };
    if (Array.isArray(expected) && expected.length) {
      issue.expected = expected;
    }
    this.issues.push(issue);
  }

  hasErrorIssues() {
    return this.issues.some((issue) => issue.severity === "error");
  }

  checkpointLeadingExpression(enabled) {
    if (!enabled || this.hasErrorIssues()) {
      return;
    }
    const end = this.previousEnd();
    if (
      Number.isInteger(end) &&
      end > 0 &&
      (this.leadingExpressionEnd == null || end > this.leadingExpressionEnd)
    ) {
      this.leadingExpressionEnd = end;
    }
  }

  matchSymbol(symbol) {
    if (this.current()?.type === "symbol" && this.current().value === symbol) {
      this.index += 1;
      return true;
    }
    return false;
  }

  matchWord(word) {
    if (this.current()?.type === "word" && searchExpressionTokenLower(this.current()) === word) {
      this.index += 1;
      return true;
    }
    return false;
  }

  startsPrimary() {
    const token = this.current();
    if (!token) {
      return false;
    }
    if (token.type === "symbol") {
      return token.value === "(" ||
        token.value === "!" ||
        (token.value === "[" && this.supportsContainerScope());
    }
    if (token.type === "word") {
      const lowered = searchExpressionTokenLower(token);
      return lowered !== "and" && lowered !== "or";
    }
    return token.type === "value";
  }

  parse() {
    const expression = this.parseOrExpression(true);
    if (!expression) {
      if (!this.hasErrorIssues()) {
        this.addIssue({
          code: "expected_term",
          expected: ["term", "("],
          message: "Expected a search term.",
        });
      }
      return null;
    }
    if (this.current()) {
      this.addIssue({
        code: "unexpected_token",
        expected: ["AND", "OR", ")"],
        message: "Unexpected token in search expression.",
      });
      return null;
    }
    return this.finalizeParsedExpression(expression);
  }

  finalizeParsedExpression(expression) {
    return expression;
  }

  parseOrExpression(checkpointTopLevel = false) {
    const first = this.parseAndExpression(checkpointTopLevel);
    if (!first) {
      return null;
    }
    const children = [first];
    while (this.current()) {
      if (this.matchWord("or")) {
        const right = this.parseAndExpression(checkpointTopLevel);
        if (!right) {
          return null;
        }
        children.push(right);
        continue;
      }
      if (!this.startsPrimary()) {
        break;
      }
      const implicitToken = this.current();
      const implicitGap = this.source.slice(this.previousEnd(), implicitToken.start);
      if (!/\s/u.test(implicitGap)) {
        break;
      }
      const checkpointBeforeImplicit = this.leadingExpressionEnd;
      const right = this.parseAndExpression(checkpointTopLevel);
      if (!right) {
        if (checkpointTopLevel && this.implicitOrIssueSeverity === "error") {
          this.leadingExpressionEnd = checkpointBeforeImplicit;
        }
        return null;
      }
      this.addIssue({
        severity: this.implicitOrIssueSeverity,
        code: "implicit_or",
        span: { start: implicitToken?.start ?? this.previousEnd(), end: this.previousEnd() },
        message: this.implicitOrMessage,
      });
      if (checkpointTopLevel && this.implicitOrIssueSeverity === "error") {
        this.leadingExpressionEnd = checkpointBeforeImplicit;
      }
      children.push(right);
    }
    return children.length === 1 ? first : this.makeGroupNode("or", children);
  }

  parseAndExpression(checkpointTopLevel = false) {
    const first = this.parseNotExpression();
    if (!first) {
      return null;
    }
    const children = [first];
    this.checkpointLeadingExpression(checkpointTopLevel);
    while (this.matchWord("and")) {
      const right = this.parseNotExpression();
      if (!right) {
        return null;
      }
      children.push(right);
      this.checkpointLeadingExpression(checkpointTopLevel);
    }
    return children.length === 1 ? first : this.makeGroupNode("and", children);
  }

  parseNotExpression() {
    const prefixNode = this.parsePrefixAliasExpression(false, null);
    if (prefixNode) {
      return prefixNode;
    }

    let negated = false;
    let negationStart = null;
    while (this.current()) {
      const token = this.current();
      const matched = token?.type === "symbol" && token.value === "!"
        ? this.matchSymbol("!")
        : token?.type === "word" && searchExpressionTokenLower(token) === "not"
          ? this.matchWord("not")
          : false;
      if (!matched) {
        break;
      }
      if (negationStart == null) {
        negationStart = token.start;
      }
      negated = !negated;
      const aliasNode = this.parsePrefixAliasExpression(negated, negationStart);
      if (aliasNode) {
        return aliasNode;
      }
    }

    const node = this.parsePrimary();
    if (!node) {
      return null;
    }
    return negated ? this.applyParsedNegation(node, negationStart) : node;
  }

  parsePrefixAliasExpression(_negated, _negationStart) {
    return null;
  }

  applyParsedNegation(node, _negationStart) {
    return node ? { ...node, negated: true } : null;
  }

  parsePrimary() {
    const openToken = this.current();
    if (this.matchSymbol("(")) {
      const node = this.parseOrExpression();
      const closeToken = this.current();
      if (!node || !this.matchSymbol(")")) {
        this.handleUnterminatedGroup(openToken);
        return null;
      }
      return this.decorateParenthesizedNode(node, openToken, closeToken);
    }
    if (this.supportsContainerScope() && this.matchSymbol("[")) {
      const node = this.parseOrExpression();
      const closeToken = this.current();
      if (!node || !this.matchSymbol("]")) {
        this.handleUnterminatedContainer(openToken);
        return null;
      }
      return this.makeContainerNode(node, openToken, closeToken);
    }
    return this.parseTerm();
  }

  supportsContainerScope() {
    return false;
  }

  handleUnterminatedGroup(_openToken) {}

  handleUnterminatedContainer(_openToken) {}

  decorateParenthesizedNode(node, _openToken, _closeToken) {
    return node;
  }

  makeContainerNode(_node, _openToken, _closeToken) {
    throw new Error("SearchExpressionBooleanParser.makeContainerNode must be implemented");
  }

  parseValueToken() {
    const token = this.current();
    if (token?.type !== "word" && token?.type !== "value") {
      return null;
    }
    this.index += 1;
    return token;
  }

  spacedValueCandidates(firstToken) {
    const initialIndex = this.index;
    const candidates = [
      {
        token: firstToken,
        nextIndex: initialIndex,
      },
    ];
    if (firstToken?.type !== "word") {
      return candidates;
    }
    const numericValue = Number(firstToken.value);
    if (/^\d+$/.test(firstToken.value) && Number.isInteger(numericValue) && numericValue > 0) {
      return candidates;
    }
    let lastToken = firstToken;
    let scanIndex = initialIndex;
    let previousEnd = firstToken.end;
    while (scanIndex < this.tokens.length) {
      const token = this.tokens[scanIndex];
      if (!searchExpressionSpacedValueCanExtend({
        source: this.source,
        firstToken,
        previousToken: lastToken,
        previousEnd,
        token,
        nextToken: this.tokens[scanIndex + 1],
      })) {
        break;
      }
      lastToken = token;
      scanIndex += 1;
      previousEnd = token.end;
      candidates.push({
        token: makeCombinedSearchExpressionValueToken(this.source, firstToken, lastToken),
        nextIndex: scanIndex,
      });
    }
    return candidates;
  }

  resolveSpacedValueToken(firstToken, resolveCandidate) {
    const candidates = this.spacedValueCandidates(firstToken);
    const resolvedCandidates = candidates.map((candidate) => ({
      ...candidate,
      resolved: resolveCandidate(searchExpressionTokenText(candidate.token)),
    }));
    const resolvedCandidate = [...resolvedCandidates]
      .reverse()
      .find((candidate) => candidate.resolved.status === "resolved");
    const selected = resolvedCandidate || resolvedCandidates[0];
    this.index = selected?.nextIndex ?? this.index;
    return selected;
  }

  parseValueListTokens() {
    if (!this.matchSymbol("[")) {
      return null;
    }
    const values = [];
    if (this.matchSymbol("]")) {
      return values;
    }
    while (this.current()) {
      const token = this.parseValueToken();
      if (!token) {
        return null;
      }
      values.push(token);
      if (this.matchSymbol("]")) {
        return values;
      }
      if (!this.matchSymbol(",")) {
        return null;
      }
    }
    return null;
  }

  makeGroupNode(_operator, _children) {
    throw new Error("SearchExpressionBooleanParser.makeGroupNode must be implemented");
  }

  parseTerm() {
    throw new Error("SearchExpressionBooleanParser.parseTerm must be implemented");
  }
}
